import axios from "axios";
import { storageService } from "./storageService";

const API_URL = process.env.REACT_APP_V5_API_URL;

const apiInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (error: unknown) => void;
  config: any;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.config.headers["Authorization"] = `Bearer ${token}`;
      prom.resolve(apiInstance(prom.config));
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Request interceptor
apiInstance.interceptors.request.use(
  (config) => {
    const token = storageService.getAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const isMultiDeviceError =
      error?.request?.responseURL?.includes("accessCode") &&
      error.response?.status === 401;

    if (isMultiDeviceError) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = storageService.getRefreshToken();

      try {
        const response = await axios.post(`${API_URL}/auth/refresh/refresh-tokens`, {
          refreshToken,
        });

        const newAccessToken = response.data.data.access.token;
        const newRefreshToken = response.data.data.refresh.token;

        storageService.setAccessToken(newAccessToken);
        storageService.setRefreshToken(newRefreshToken);

        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);

        return apiInstance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        storageService.clearAll();
        window.location.href = "/auth/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    } else if (error.response?.status === 402) {
      storageService.clearAll();
      window.location.href = "/auth/login";
    }

    return Promise.reject(error);
  }
);

export default apiInstance;
